import { message, Slider, Card } from "antd";
import { EditOutlined } from "@ant-design/icons";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import { FaCube } from "react-icons/fa";
import { signTypes } from "../data/fakeSignals";
import TrafficSignMarker from "../components/TrafficSignMarker";
import { FaMapLocationDot, FaStreetView, FaLocationArrow } from "react-icons/fa6";
import { MapContainer, useMap, useMapEvents, GeoJSON, FeatureGroup } from "react-leaflet";
import MapMeasurementIndicators from "../components/MapMeasurementIndicators.jsx";
import MarkerClusterGroup from "react-leaflet-cluster";
import "react-leaflet-cluster/dist/assets/MarkerCluster.css";
import "react-leaflet-cluster/dist/assets/MarkerCluster.Default.css";
import { TlGreenWaveControls } from "../../tl-green-wave";
import * as THREE from "three";
import { PLYLoader } from "three/examples/jsm/loaders/PLYLoader.js";
import DeckGL from "@deck.gl/react";
import { IconLayer, PointCloudLayer, LineLayer, PolygonLayer, TextLayer } from "@deck.gl/layers";
import { OrbitView, COORDINATE_SYSTEM } from "@deck.gl/core";
import { getClientType } from "../../../services/clientConfig";
import { fetchClientJSON } from "../../../services/clientDataService";
import { useLoading } from "../../../contexts/LoadingContext";

import {
  createSignal,
  fetchSignals,
  updateSignal,
  deleteSignal,
} from "../APIs";
import CreateSignal from "../components/Forms/CreateSignal.jsx";
import SignalFixExtras from "../components/SignalFixExtras.jsx";
import RmsSegmentTooltip from "../components/RmsSegmentTooltip.jsx";
import { colorRmsSegments } from "../utils/rmsColors";
import DeleteConfirmModal from "../components/DeleteConfirmModal.jsx";
import MeasurementTools from "../components/MeasurementTools.jsx";
import ModuleSlider from "../../../components/map/ModuleSlider.jsx";
import Measurement3DTools from "../components/Measurement3DTools.jsx";
import SelectedPointsDisplay from "../components/SelectedPointsDisplay.jsx";
import Ply3DMarkers from "../components/Ply3DMarkers.jsx";
import CampaignSummaryPanel from "../components/CampaignSummaryPanel.jsx";
import SignalFixLegend from "../components/SignalFixLegend.jsx";
import SignalFixGeometryLayer from "../components/SignalFixGeometryLayer.jsx";
import SIGNAL_FIX_V2_SAMPLE from "../data/signalFixV2Sample.js";
import {
  normalizeUploadedElements,
  mergeElements,
  centroidOf,
  translateElements,
  SIGNALFIX_ELEMENTS_PATH,
} from "../data/signalFixAdapter.js";
import { CATEGORY_FILTERS, isSignInEnabledCategories } from "../data/signalElements";
import { DataLoadingScreen } from "../../../components/common";
import {
  calculateDistance3D,
  calculatePolygonArea,
  calculateVolume,
  formatDistance,
  formatArea,
  formatVolume,
  getMidpoint
} from "../utils/measurementUtils.js";
import "../styles/signal-fix.css";
import "../styles/signal-fix-components.css";
import "../styles/ply3DTooltip.css";
import "../styles/ply3DMarkers.css";
import "../styles/ant-overrides.css"; // Fix for ant-image-preview z-index
import "../styles/components/measurement-tools.css";
import "../styles/components/selected-points-display.css";
import "../styles/components/map-measurement-indicators.css";
import "../../../styles/mappa.css"; // For theme toggle styles
import { reverseGeocode, searchGeocode } from "../../../tools/geocoding/geocodingApi";
import { TraffixTileLayer, ThemeToggleButton } from "../../../common/map";

// Removed texture imports - no longer displaying signs in 3D view

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// The v2 element dataset is loaded per client_type from the uploads server
// (see SIGNALFIX_ELEMENTS_PATH). This bundled sample is only used as a fallback
// when that file is missing/empty, so the new icons / geometry / legend are
// still visible. Set to false to disable the fallback entirely.
const SHOW_V2_SAMPLE = true;

// Elements used while the uploads file has not resolved yet, or as a fallback.
const v2SampleFallback = () => (SHOW_V2_SAMPLE ? SIGNAL_FIX_V2_SAMPLE : []);

// Numbered cluster badge whose size and colour scale with the element count,
// per the v2 spec (denser areas read as larger / warmer badges).
const createClusterIcon = (cluster) => {
  const count = cluster.getChildCount();
  let size = 38;
  let tier = "sm";
  if (count >= 50) {
    size = 58;
    tier = "lg";
  } else if (count >= 15) {
    size = 48;
    tier = "md";
  }
  return L.divIcon({
    html: `<div class="signalfix-cluster signalfix-cluster-${tier}"><span>${count}</span></div>`,
    className: "signalfix-cluster-wrapper",
    iconSize: L.point(size, size, true),
  });
};

// A sign is drawn by the geometry layer when it carries a polyline geometry.
const hasRenderableGeometry = (sign) => Array.isArray(sign?.geometry) && sign.geometry.length >= 2;

function CurrentLocationButton({ style }) {
  const map = useMap();
  const markerRef = useRef(null);

  const handleClick = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        map.flyTo([latitude, longitude], 18);

        if (markerRef.current) {
          map.removeLayer(markerRef.current);
        }

        const icon = L.icon({
          iconUrl: require("leaflet/dist/images/marker-icon.png"),
          iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
          shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
          iconSize: [25, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowSize: [41, 41],
        });

        const newMarker = L.marker([latitude, longitude], { icon })
          .addTo(map)
          .bindPopup("You are here")
          .openPopup();

        markerRef.current = newMarker;
      },
      (error) => {
        console.error("Error getting location:", error);
        alert("Unable to get your location.");
      }
    );
  };

  return (
    <button onClick={handleClick} title="Go to my location" className="signal-fix-location-button">
      <FaLocationArrow size={18} />
    </button>
  );
}

function ZoomButtons() {
  const map = useMap();

  return (
    <div className="signal-fix-zoom-buttons-container">
      <button onClick={() => map.zoomIn()} title="Zoom in" className="signal-fix-zoom-button">
        +
      </button>
      <button onClick={() => map.zoomOut()} title="Zoom out" className="signal-fix-zoom-button">
        -
      </button>
    </div>
  );
}

function CombinedViewToggleButton({ mode, onCycle }) {
  const title =
    mode === "map"
      ? "Switch to Satellite View"
      : mode === "satellite"
        ? "Switch to Map View"
        : "3D Sign View";

  return (
    <button onClick={onCycle} title={title} className="signal-fix-view-toggle-button">
      {mode === "map" && <FaMapLocationDot className="map-view-icon" />}
      {mode === "satellite" && <FaStreetView className="map-view-icon" />}
      {mode === "pointcloud" && <FaCube className="map-view-icon" />}
    </button>
  );
}



function MapClickDetector({ onMapClick, onMapRightClick, measurementMode, measurementPoints, onCompleteMeasurement }) {
  useMapEvents({
    click: (e) => {
      onMapClick(e);
    },
    contextmenu: (e) => {
      // Handle right-click for completing area/volume measurements
      if ((measurementMode === 'area' || measurementMode === 'volume') && measurementPoints.length >= 3) {
        e.originalEvent.preventDefault();
        onCompleteMeasurement && onCompleteMeasurement(measurementMode, measurementPoints);
      }
      onMapRightClick && onMapRightClick(e);
    }
  });
  return null;
}

function ZoomLimitsController() {
  const map = useMap();

  useEffect(() => {
    // Allow the widest possible zoom range in every view:
    //  - zoom in to level 26 (beyond native tiles the last tiles are upscaled,
    //    see maxNativeZoom in TraffixTileLayer)
    //  - zoom out to level 0 (whole world)
    map.setMinZoom(0);
    map.setMaxZoom(26);
  }, [map]);

  return null;
}

// Deck.gl Point Cloud Viewer with Three.js color extraction
function DeckGLPointCloudOverlay({
  showPointCloud,
  pointSize = 0.4,
  onPointSizeChange,
  selectedPlyLocation = null
}) {
  // Initialize with a default viewState to prevent null errors
  const [viewState, setViewState] = useState({
    target: [0, 0, 0],
    zoom: 12,  // Very close initial zoom
    rotationX: 60,  // Tilt camera to look from front (positive = looking up from below)
    rotationOrbit: 0,  // No orbital rotation, face straight
    minZoom: -2,
    maxZoom: 20  // Allow even more zoom in
  });
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [plyData, setPlyData] = useState(null);
  const plyLoadingRef = useRef(false);

  // Measurement states - 3D view only supports point-to-point (distance) measurement
  const [measurementMode, setMeasurementMode] = useState(null); // 'distance' only for 3D view
  const [measurementPoints, setMeasurementPoints] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  const [tempPoint, setTempPoint] = useState(null);
  const [selectedCloudPoints, setSelectedCloudPoints] = useState([]); // Store actual clicked point cloud points with their original data

  // Hover tooltip state for 3D view (matching old design)
  const [hoveredSign, setHoveredSign] = useState(null);
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, content: null });

  // Effect to load PLY when selectedPlyLocation changes
  useEffect(() => {
    if (!selectedPlyLocation || !showPointCloud) {
      // Clear PLY data when no PLY location is selected
      if (!selectedPlyLocation) {
        setPlyData(null);
        plyLoadingRef.current = false;
      }
      return;
    }

    // Prevent multiple simultaneous loads
    if (plyLoadingRef.current) {
      console.log("PLY already loading, skipping...");
      return;
    }
        const clientType = getClientType();

    // Load external PLY file from the server
    const plyUrl = `https://www.traffixcloud.com/upload/signs_ply/${clientType}/${selectedPlyLocation.name}`;
    console.log("Loading PLY:", selectedPlyLocation.name, "from URL:", plyUrl);

    plyLoadingRef.current = true;
    setLoading(true);
    setLoadingProgress(0);
    const loader = new PLYLoader();

    loader.load(
      plyUrl,
      (geometry) => {
        const positions = geometry.attributes.position.array;
        const colors = geometry.attributes.color?.array;
        const pointCount = geometry.attributes.position.count;

        // Calculate centroid for the sign PLY
        let cx = 0, cy = 0, cz = 0;
        for (let i = 0; i < positions.length; i += 3) {
          cx += positions[i];
          cy += positions[i + 1];
          cz += positions[i + 2];
        }
        cx /= pointCount;
        cy /= pointCount;
        cz /= pointCount;

        // Convert to deck.gl format with centered positions
        const data = [];
        for (let i = 0; i < pointCount; i++) {
          const position = [
            positions[i * 3] - cx,
            positions[i * 3 + 1] - cy,
            positions[i * 3 + 2] - cz,
          ];

          let color;
          if (colors) {
            color = [
              Math.round(colors[i * 3] * 255),
              Math.round(colors[i * 3 + 1] * 255),
              Math.round(colors[i * 3 + 2] * 255),
              255
            ];
          } else {
            color = [255, 255, 255, 255];
          }

          data.push({ position, color });
        }

        setPlyData(data);

        // Adjust view to focus on the sign
        geometry.computeBoundingBox();
        const box = geometry.boundingBox;
        const size = new THREE.Vector3();
        box.getSize(size);
        const maxDim = Math.max(size.x, size.y, size.z);

        setViewState({
          target: [0, 0, 0],
          zoom: Math.log2(15 / maxDim) + 6, // Very close initial zoom
          rotationX: 60,  // Tilt camera to look from front
          rotationOrbit: 0, // Face straight front
          minZoom: -2,
          maxZoom: 20  // Allow even more zoom in
        });

        plyLoadingRef.current = false;
        setLoading(false);
      },
      (progress) => {
        if (progress.total > 0) {
          const percentComplete = Math.round((progress.loaded / progress.total) * 100);
          setLoadingProgress(percentComplete);
        }
      },
      (error) => {
        console.error("Error loading sign PLY:", error);
        // Handle error loading PLY
        setPlyData(null);
        plyLoadingRef.current = false;
        setLoading(false);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlyLocation, showPointCloud]);

  // Measurement handlers with useCallback to prevent re-renders
  const handlePointCloudClick = useCallback((info) => {
    // Add error handling to prevent null reference errors
    if (!info || !measurementMode) return;

    // Make sure we clicked on an actual point, not empty space
    if (!info.object) {
      console.warn('No point cloud object clicked');
      return;
    }

    // Get the exact position of the clicked point from the point cloud data
    const clickedPoint = info.object.position || info.coordinate;

    // Ensure the position is valid
    if (!clickedPoint || !Array.isArray(clickedPoint)) {
      console.warn('Invalid point position:', info);
      return;
    }

    // Store the actual clicked point cloud data with the exact position
    const cloudPointData = {
      position: clickedPoint,  // Use the exact point position from the object
      color: info.object.color || [255, 255, 255, 255],
      index: info.index,
      object: info.object,
      // Store the original point data for precise measurement
      originalData: info.object
    };

    // 3D view only supports point-to-point distance measurement
    if (measurementMode === 'distance') {
      if (measurementPoints.length < 2) {
        const newPoints = [...measurementPoints, clickedPoint];
        const newCloudPoints = [...selectedCloudPoints, cloudPointData];
        setMeasurementPoints(newPoints);
        setSelectedCloudPoints(newCloudPoints);

        if (newPoints.length === 2) {
          const distance = calculateDistance3D(newPoints[0], newPoints[1]);
          const newMeasurement = {
            type: 'distance',
            points: newPoints,
            cloudPoints: newCloudPoints,
            value: distance,
            formattedValue: formatDistance(distance)
          };
          setMeasurements(prev => [...prev, newMeasurement]);
          setMeasurementPoints([]);
          setSelectedCloudPoints([]);
        }
      }
    }
  }, [measurementMode, measurementPoints, selectedCloudPoints]);

  const clearMeasurements = () => {
    setMeasurements([]);
    setMeasurementPoints([]);
    setSelectedCloudPoints([]);
  };

  const undoLastMeasurement = () => {
    if (measurements.length > 0) {
      setMeasurements(measurements.slice(0, -1));
    }
  };

  const removeSelectedPoint = (index) => {
    const newPoints = [...measurementPoints];
    const newCloudPoints = [...selectedCloudPoints];
    newPoints.splice(index, 1);
    newCloudPoints.splice(index, 1);
    setMeasurementPoints(newPoints);
    setSelectedCloudPoints(newCloudPoints);
  };

  const clearAllSelectedPoints = () => {
    setMeasurementPoints([]);
    setSelectedCloudPoints([]);
  };

  // Memoize layers to prevent re-creation on every render
  const layers = useMemo(() => {
    // Only show PLY data when available
    if (!plyData || !Array.isArray(plyData)) return [];

    const pointCloudLayer = new PointCloudLayer({
      id: "point-cloud-layer",
      data: plyData,
      getPosition: (d) => d.position,
      getColor: (d) => d.color || [255, 255, 255, 255],
      getNormal: [0, 0, 1],
      coordinateSystem: COORDINATE_SYSTEM.CARTESIAN,
      coordinateOrigin: [0, 0, 0],
      sizeUnits: "pixels",
      pointSize: pointSize,
      pickable: measurementMode !== null, // Only pickable when in measurement mode
      autoHighlight: measurementMode !== null, // Highlight point on hover
      highlightColor: [255, 255, 0, 128], // Yellow highlight on hover
      onClick: measurementMode ? (info) => {
        try {
          handlePointCloudClick(info);
        } catch (error) {
          console.error('Error handling point cloud click:', error);
        }
      } : undefined,
      onHover: (info, event) => {
        // Always handle hover for tooltip display
        if (info.object) {
          // Show tooltip for hovered point
          setHoveredSign(info.object);
          setTooltip({
            visible: true,
            x: event.offsetX || info.x,
            y: event.offsetY || info.y,
            content: {
              id: `Point ${info.index}`,
              type: selectedPlyLocation?.name || 'Point Cloud',
              position: info.object.position
            }
          });

          // Change cursor in measurement mode
          if (measurementMode) {
            document.body.style.cursor = 'crosshair';
          }
        } else {
          // Hide tooltip when not hovering
          setHoveredSign(null);
          setTooltip({ visible: false, x: 0, y: 0, content: null });
          document.body.style.cursor = 'default';
        }
      },
      parameters: {
        // Improve depth testing for better point selection
        depthTest: true,
        blend: true,
        blendFunc: [770, 771], // GL.SRC_ALPHA, GL.ONE_MINUS_SRC_ALPHA
        blendEquation: 32774 // GL.FUNC_ADD
      }
    });


    // Create measurement visualization layers
    const measurementLayers = [];

    // Add highlight layer for selected cloud points - these are the EXACT points clicked
    if (selectedCloudPoints.length > 0) {
      // Create a highlight layer showing the exact points that were clicked
      const highlightLayer = new PointCloudLayer({
        id: 'selected-cloud-points-highlight',
        data: selectedCloudPoints.map((cp, i) => ({
          position: cp.position,
          color: [255, 0, 0, 255], // Red for exact clicked points
          index: i
        })),
        getPosition: d => d.position,
        getColor: d => d.color,
        getNormal: [0, 0, 1],
        coordinateSystem: COORDINATE_SYSTEM.CARTESIAN,
        coordinateOrigin: [0, 0, 0],
        sizeUnits: 'pixels',
        pointSize: pointSize * 4, // Make selected points 4x larger for visibility
        pickable: false
      });
      measurementLayers.push(highlightLayer);

      // Add a glow effect around selected points
      const glowLayer = new PointCloudLayer({
        id: 'selected-cloud-points-glow',
        data: selectedCloudPoints.map((cp, i) => ({
          position: cp.position,
          color: [255, 255, 0, 180], // Yellow glow
          index: i
        })),
        getPosition: d => d.position,
        getColor: d => d.color,
        getNormal: [0, 0, 1],
        coordinateSystem: COORDINATE_SYSTEM.CARTESIAN,
        coordinateOrigin: [0, 0, 0],
        sizeUnits: 'pixels',
        pointSize: pointSize * 8, // Larger glow effect
        pickable: false
      });
      measurementLayers.push(glowLayer);

      // Add sphere markers at the exact clicked positions
      const sphereMarkers = new IconLayer({
        id: 'clicked-point-spheres',
        data: selectedCloudPoints.map((cp, i) => ({
          position: cp.position,
          index: i + 1
        })),
        getIcon: () => ({
          url: 'data:image/svg+xml;base64,' + btoa(`
            <svg width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
              <circle cx="8" cy="8" r="7" fill="#FF0000" stroke="white" stroke-width="1" opacity="0.9"/>
              <circle cx="8" cy="8" r="3" fill="white"/>
            </svg>
          `),
          width: 16,
          height: 16
        }),
        getPosition: d => d.position,
        getSize: 1,
        sizeUnits: 'pixels',
        sizeScale: 1,
        billboard: true,
        pickable: false
      });
      measurementLayers.push(sphereMarkers);
    }

    // Add active measurement points with enhanced visualization
    if (measurementPoints.length > 0) {
      // Enhanced points layer for measurement points
      const pointsLayer = new IconLayer({
        id: 'measurement-points',
        data: measurementPoints.map((p, i) => ({ position: p, index: i + 1 })),
        getIcon: (d) => ({
          url: 'data:image/svg+xml;base64,' + btoa(`
            <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                  <feDropShadow dx="0" dy="1" stdDeviation="2" flood-opacity="0.3"/>
                </filter>
              </defs>
              <circle cx="16" cy="16" r="14" fill="#A1E430" stroke="white" stroke-width="3" filter="url(#shadow)"/>
              <text x="16" y="20" text-anchor="middle" fill="white" font-size="12" font-weight="bold">${d.index}</text>
            </svg>
          `),
          width: 32,
          height: 32
        }),
        getPosition: d => d.position,
        getSize: 1,
        sizeUnits: 'pixels',
        sizeScale: 1,
        billboard: true,
        pickable: false
      });
      measurementLayers.push(pointsLayer);

      // Add pulsing effect layer for active points
      const pulseLayer = new IconLayer({
        id: 'measurement-points-pulse',
        data: measurementPoints.map((p, i) => ({ position: p, index: i + 1 })),
        getIcon: () => ({
          url: 'data:image/svg+xml;base64,' + btoa(`
            <svg width="48" height="48" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
              <circle cx="24" cy="24" r="20" fill="none" stroke="#A1E430" stroke-width="2" opacity="0.4">
                <animate attributeName="r" from="10" to="20" dur="1.5s" repeatCount="indefinite"/>
                <animate attributeName="opacity" from="0.6" to="0" dur="1.5s" repeatCount="indefinite"/>
              </circle>
            </svg>
          `),
          width: 48,
          height: 48
        }),
        getPosition: d => d.position,
        getSize: 1,
        sizeUnits: 'pixels',
        sizeScale: 1,
        billboard: true,
        pickable: false
      });
      measurementLayers.push(pulseLayer);

      // Add connection lines between sequential points for all measurement modes
      if (measurementPoints.length >= 2) {
        for (let i = 0; i < measurementPoints.length - 1; i++) {
          const connectionLine = new LineLayer({
            id: `connection-line-${i}`,
            data: [{
              sourcePosition: measurementPoints[i],
              targetPosition: measurementPoints[i + 1]
            }],
            getSourcePosition: d => d.sourcePosition,
            getTargetPosition: d => d.targetPosition,
            getColor: [161, 228, 48, 200],
            getWidth: 2,
            getDashArray: [8, 4],
            pickable: false
          });
          measurementLayers.push(connectionLine);
        }
      }

      // Line layer for distance measurement with enhanced styling (3D view only supports distance)
      if (measurementMode === 'distance' && measurementPoints.length === 1) {
        // Show line from first point to cursor position if available
        // This provides visual feedback while selecting the second point
      }

      if (measurementMode === 'distance' && measurementPoints.length === 2) {
        const lineLayer = new LineLayer({
          id: 'measurement-line',
          data: [{ sourcePosition: measurementPoints[0], targetPosition: measurementPoints[1] }],
          getSourcePosition: d => d.sourcePosition,
          getTargetPosition: d => d.targetPosition,
          getColor: [161, 228, 48],
          getWidth: 4,
          pickable: false
        });
        measurementLayers.push(lineLayer);

        // Add distance label in 3D at midpoint
        const midpoint = getMidpoint(measurementPoints[0], measurementPoints[1]);
        const distance = calculateDistance3D(measurementPoints[0], measurementPoints[1]);
        const distanceLabelLayer = new TextLayer({
          id: 'active-distance-label',
          data: [{ position: midpoint, text: formatDistance(distance) }],
          getPosition: d => d.position,
          getText: d => d.text,
          getSize: 16,
          getColor: [255, 255, 255],
          getBackgroundColor: [161, 228, 48],
          billboard: true,
          getTextAnchor: 'middle',
          getAlignmentBaseline: 'center',
          backgroundColor: true,
          backgroundPadding: [8, 4]
        });
        measurementLayers.push(distanceLabelLayer);
      }
    }

    // Add completed measurements visualization
    measurements.forEach((measurement, idx) => {
      // Add points for completed measurements
      const completedPointsLayer = new IconLayer({
        id: `completed-points-${idx}`,
        data: measurement.points.map((p, i) => ({ position: p, index: i + 1 })),
        getIcon: (d) => ({
          url: 'data:image/svg+xml;base64,' + btoa(`
            <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" fill="#4CAF50" stroke="white" stroke-width="2"/>
              <text x="12" y="16" text-anchor="middle" fill="white" font-size="10" font-weight="bold">${d.index}</text>
            </svg>
          `),
          width: 24,
          height: 24
        }),
        getPosition: d => d.position,
        getSize: 1,
        sizeUnits: 'pixels',
        sizeScale: 1,
        billboard: true,
        pickable: false
      });
      measurementLayers.push(completedPointsLayer);

      if (measurement.type === 'distance') {
        const lineLayer = new LineLayer({
          id: `completed-line-${idx}`,
          data: [{ sourcePosition: measurement.points[0], targetPosition: measurement.points[1] }],
          getSourcePosition: d => d.sourcePosition,
          getTargetPosition: d => d.targetPosition,
          getColor: [100, 200, 100],
          getWidth: 3,
          pickable: false
        });
        measurementLayers.push(lineLayer);

        // Add text label at midpoint with enhanced styling
        const midpoint = getMidpoint(measurement.points[0], measurement.points[1]);
        const textLayer = new TextLayer({
          id: `distance-label-${idx}`,
          data: [{ position: midpoint, text: measurement.formattedValue }],
          getPosition: d => d.position,
          getText: d => d.text,
          getSize: 14,
          getColor: [255, 255, 255],
          getBackgroundColor: [76, 175, 80],
          billboard: true,
          getTextAnchor: 'middle',
          getAlignmentBaseline: 'center',
          backgroundColor: true,
          backgroundPadding: [6, 3],
          fontWeight: 600
        });
        measurementLayers.push(textLayer);
      } else if (measurement.type === 'area' || measurement.type === 'volume') {
        const polygonLayer = new PolygonLayer({
          id: `completed-polygon-${idx}`,
          data: [{ polygon: [measurement.points] }],
          getPolygon: d => d.polygon,
          getFillColor: measurement.type === 'volume' ? [76, 175, 80, 50] : [76, 175, 80, 30],
          getLineColor: [76, 175, 80],
          getLineWidth: 2,
          pickable: false
        });
        measurementLayers.push(polygonLayer);

        // Add edge lines for better visibility
        for (let i = 0; i < measurement.points.length; i++) {
          const nextIndex = (i + 1) % measurement.points.length;
          const edgeLineLayer = new LineLayer({
            id: `completed-edge-${idx}-${i}`,
            data: [{
              sourcePosition: measurement.points[i],
              targetPosition: measurement.points[nextIndex]
            }],
            getSourcePosition: d => d.sourcePosition,
            getTargetPosition: d => d.targetPosition,
            getColor: [76, 175, 80],
            getWidth: 2,
            pickable: false
          });
          measurementLayers.push(edgeLineLayer);
        }

        // Add text label at centroid with enhanced styling
        const centroid = measurement.points.reduce((acc, p) => [
          acc[0] + p[0] / measurement.points.length,
          acc[1] + p[1] / measurement.points.length,
          acc[2] + p[2] / measurement.points.length
        ], [0, 0, 0]);

        const textLayer = new TextLayer({
          id: `area-label-${idx}`,
          data: [{ position: centroid, text: measurement.formattedValue }],
          getPosition: d => d.position,
          getText: d => d.text,
          getSize: 14,
          getColor: [255, 255, 255],
          getBackgroundColor: [76, 175, 80],
          billboard: true,
          getTextAnchor: 'middle',
          getAlignmentBaseline: 'center',
          backgroundColor: true,
          backgroundPadding: [6, 3],
          fontWeight: 600
        });
        measurementLayers.push(textLayer);

        // Add measurement type indicator
        const typeIndicatorLayer = new TextLayer({
          id: `type-indicator-${idx}`,
          data: [{
            position: [centroid[0], centroid[1], centroid[2] + 0.5],
            text: measurement.type === 'volume' ? `Volume (h=${measurement.height}m)` : 'Area'
          }],
          getPosition: d => d.position,
          getText: d => d.text,
          getSize: 11,
          getColor: [150, 150, 150],
          billboard: true,
          getTextAnchor: 'middle',
          getAlignmentBaseline: 'center'
        });
        measurementLayers.push(typeIndicatorLayer);
      }
    });

    const finalLayers = [pointCloudLayer, ...measurementLayers].filter(Boolean);

    return finalLayers;
  }, [plyData, pointSize, measurementPoints, measurements, measurementMode, handlePointCloudClick, selectedCloudPoints, selectedPlyLocation?.name, setHoveredSign, setTooltip]);

  // Removed sign preparation - no longer displaying signs in 3D view

  // Removed city PLY loading - we only load sign PLYs now





  if (!showPointCloud) {
    return null;
  }
  
  // Guard clause against missing PLY data while loading so DeckGL doesn't crash
  if (loading || (!plyData && selectedPlyLocation)) {
    return (
      <DataLoadingScreen 
        visible={true} 
        percentage={loadingProgress} 
        text="Caricamento Modello 3D..." 
      />
    );
  }

  return (
    <div className="signal-fix-pointcloud-container">
      <DeckGL
        views={new OrbitView({
          orbitAxis: 'Z'  // Rotate around Z axis for proper orientation
        })}
        viewState={viewState}
        onViewStateChange={({ viewState }) => setViewState(viewState)}
        controller={true}
        layers={layers}
        getCursor={() => 'grab'}
        style={{ width: "100%", height: "100%", backgroundColor: "#000000" }}
      />

      {/* 3D Measurement Tools Panel - Only Distance */}
      <Measurement3DTools
        measurementMode={measurementMode}
        onModeChange={setMeasurementMode}
        measurements={measurements}
        onClearMeasurements={clearMeasurements}
        onUndo={undoLastMeasurement}
        canUndo={measurements.length > 0}
      />

      {/* Selected Points Display */}
      <SelectedPointsDisplay
        measurementPoints={measurementPoints}
        onRemovePoint={removeSelectedPoint}
        measurementMode={measurementMode}
        onClearAllPoints={clearAllSelectedPoints}
      />


      {/* 3D Point Cloud Filter Panel */}
      <div className="signal-fix-pointcloud-filter-panel" >
        <h3 className="signal-fix-pointcloud-filter-title">
          3D Point Cloud Filters
        </h3>

        {/* Current PLY location indicator */}
        {selectedPlyLocation && (
          <div style={{
            padding: '10px',
            background: 'rgba(161, 228, 48, 0.1)',
            borderRadius: '6px',
            marginBottom: '15px',
            borderLeft: '3px solid #A1E430'
          }}>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
              Point Cloud View
            </div>
            <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#A1E430' }}>
              {selectedPlyLocation.name}
            </div>
            <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
              {selectedPlyLocation.description}
            </div>
          </div>
        )}

        <div>
          <div className="signal-fix-pointcloud-filter-row">
            <span className="signal-fix-pointcloud-filter-label">Point Size</span>
            <span className="signal-fix-pointcloud-filter-value">
              {pointSize.toFixed(2)}
            </span>
          </div>
          <Slider
            min={0.2}
            max={2}
            step={0.05}
            value={pointSize}
            onChange={onPointSizeChange}
            tooltip={{ formatter: (value) => `${value.toFixed(2)}` }}
            trackStyle={{ backgroundColor: "#A1E430" }}
            handleStyle={{ borderColor: "#A1E430" }}
          />
        </div>
      </div>

      {/* Tooltip for 3D point cloud - Old design from backup */}
      {tooltip.visible && tooltip.content && (() => {
        return (
          <div className="ply-3d-side-card">
            <div className="ply-3d-tooltip-content">
              <button
                className="ply-3d-tooltip-close"
                onClick={(e) => {
                    e.stopPropagation();
                    setTooltip({ visible: false, x: 0, y: 0, content: null });
                }}
              >
                ×
              </button>
              <div className="ply-3d-popup">
                <h3 style={{ color: '#1a1a2e', fontSize: '18px', fontWeight: 'bold' }}>
                  {tooltip.content.id}
                </h3>
                
                <div style={{ marginBottom: '15px', color: '#666' }}>
                  <span style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>Type:</span>
                  <div style={{ color: 'var(--primary-color)', fontSize: '16px', fontWeight: '600' }}>
                    {tooltip.content.type}
                  </div>
                </div>

                <div style={{ 
                    padding: '12px', 
                    background: '#f8f9fa', 
                    borderRadius: '8px',
                    border: '1px solid #eee'
                }}>
                  <div style={{ fontSize: '11px', color: '#999', marginBottom: '4px' }}>3D POSIZIONE</div>
                  <div style={{ fontSize: '13px', fontFamily: 'monospace', color: '#333' }}>
                    X: {tooltip.content.position[0].toFixed(3)}<br/>
                    Y: {tooltip.content.position[1].toFixed(3)}<br/>
                    Z: {tooltip.content.position[2].toFixed(3)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

    </div>
  );
}

function SignalFix() {
  const { t } = useTranslation();
  const [filterActivated, setFilterActivated] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [searchValue, setSearchValue] = useState("");
  const [searchOptions, setSearchOptions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [signs, setSigns] = useState([]);
  const [filteredSigns, setFilteredSigns] = useState([]);
  const [apiSigns, setApiSigns] = useState([]); // signals from /signal/all
  const [v2Elements, setV2Elements] = useState(null); // v2 elements from uploads (null = not resolved yet)
  const [v2FromUploads, setV2FromUploads] = useState(false); // true when v2Elements came from the uploads file
  // High-level category filter (lives in the legend); all families on by default.
  const [enabledCategories, setEnabledCategories] = useState(() =>
    Object.fromEntries(CATEGORY_FILTERS.map((c) => [c.key, true]))
  );
  const [selectedSign, setSelectedSign] = useState(null);
  const [streetName, setStreetName] = useState("");
  const [loadingStreet, setLoadingStreet] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [addNew, setAddNew] = useState(false);
  const [isCreatingSignal, setIsCreatingSignal] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [mapView, setMapView] = useState("map");
  const [mapTheme, setMapTheme] = useState('dark'); // 'dark' or 'light'
  const [showSegmentiRms, setShowSegmentiRms] = useState(true); // Visible by default
  const [segmentiRmsGeoJson, setSegmentiRmsGeoJson] = useState(null);
  const [campaignInfo, setCampaignInfo] = useState({
    distance: null,
    location: null,
    date: null,
    loading: true,
  }); // "Campagna di rilievo" summary (from car_path_info.json)
  const [pointCloudSize, setPointCloudSize] = useState(0.4);
  const [mapCenter, setMapCenter] = useState([44.432401344516244, 8.88945112875312]); // Default center
  const [initialMapZoom] = useState(15); // Store initial zoom level
  const [selectedPlyLocation, setSelectedPlyLocation] = useState(null);
  const [show3DMarkers, setShow3DMarkers] = useState(true);
  const mapRef = useRef(null);
  const searchMarkerRef = useRef(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [signToDelete, setSignToDelete] = useState(null);
  const [isDeletingSignal, setIsDeletingSignal] = useState(false);

  // Map measurement states
  const [mapMeasurementMode, setMapMeasurementMode] = useState(null);
  const [mapMeasurementPoints, setMapMeasurementPoints] = useState([]);

  // TL Green Wave style drawing states for SignalFix
  const [tlgwIsDrawing, setTlgwIsDrawing] = useState(false);
  const [tlgwIsEditing, setTlgwIsEditing] = useState(false);
  const [tlgwPolygonPoints, setTlgwPolygonPoints] = useState([]);
  const tlgwFeatureGroupRef = useRef(null);
  const tlgwDrawnLayerRef = useRef(null);
  const tlgwDrawHandlerRef = useRef(null);
  const tlgwIsEditingRef = useRef(false);
  const [mapMeasurements, setMapMeasurements] = useState([]);
  const [tlgwAnimationStates, setTlgwAnimationStates] = useState(null);
  const [tlgwTrafficLights, setTlgwTrafficLights] = useState([]);
  const [tlgwIsSelecting, setTlgwIsSelecting] = useState(false);
  const [tlgwSelectedLights, setTlgwSelectedLights] = useState([]);

  // Load road-condition (segmenti RMS) data and campaign summary on mount.
  // The vehicle path is represented by the colour-coded segmenti RMS line, so
  // the plain Car_gps overlay is no longer loaded.
  useEffect(() => {
    const clientType = getClientType();

    fetchClientJSON(clientType, 'signal_fix/segmenti_rms.geojson')
      .then(data => {
        if (data && data.features && data.features.length > 0) {
          setSegmentiRmsGeoJson(colorRmsSegments(data));
        } else {
          console.error("Failed to load segmenti RMS data - invalid structure:", data);
        }
      })
      .catch(error => {
        console.error("Error fetching segmenti RMS data:", error);
      });

    // Campaign summary (monitored distance / location / date) for the panel
    // that replaces the old "Percorso Auto" popup.
    fetchClientJSON(clientType, 'signal_fix/car_path_info.json')
      .then(data => {
        setCampaignInfo({
          distance: data?.distance ?? null,
          location: data?.location ?? null,
          date: data?.date ?? null,
          loading: false,
        });
      })
      .catch(error => {
        console.error("Error fetching car path info:", error);
        setCampaignInfo(prev => ({ ...prev, loading: false }));
      });

    // v2 element dataset (per client_type) — retrieved the same way as the
    // other signal_fix files. The structure may vary, so it is normalised by
    // the adapter; fall back to the bundled sample if the file is absent.
    fetchClientJSON(clientType, SIGNALFIX_ELEMENTS_PATH)
      .then(data => {
        const normalized = normalizeUploadedElements(data);
        if (normalized.length > 0) {
          setV2Elements(normalized);
          setV2FromUploads(true);
        } else {
          setV2Elements(v2SampleFallback());
          setV2FromUploads(false);
        }
      })
      .catch(error => {
        console.warn("SignalFix elements not available from uploads, using sample fallback:", error?.message);
        setV2Elements(v2SampleFallback());
        setV2FromUploads(false);
      });
  }, []);

  const cycleViewMode = () => {
    // Only toggle between map and satellite views
    // Point cloud view is only activated via PLY marker selection
    if (mapView === "pointcloud") {
      // If in 3D view, go back to map
      setMapView("map");
      setSelectedPlyLocation(null);
      setShow3DMarkers(true);

      // Reset map to initial center and zoom
      if (mapRef.current) {
        mapRef.current.setView(mapCenter, initialMapZoom);
      }
    } else {
      // Toggle between map and satellite
      setMapView((prev) => prev === "map" ? "satellite" : "map");
    }
  };

  const fetchData = async () => {
    const result = await fetchSignals();
    setApiSigns(Array.isArray(result) ? result : []);
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Combine the DB-backed signals (previous data) with the v2 elements loaded
  // from uploads (falling back to the bundled sample until/if the file loads).
  // The bundled demo has no real location, so it is shifted next to the real
  // data centroid; real uploaded data keeps its own coordinates.
  useEffect(() => {
    let extras = v2Elements ?? v2SampleFallback();
    if (!v2FromUploads && extras.length > 0 && apiSigns.length > 0) {
      const target = centroidOf(apiSigns);
      const src = centroidOf(extras);
      if (target && src) {
        extras = translateElements(extras, target[0] - src[0], target[1] - src[1]);
      }
    }
    const combined = mergeElements(apiSigns, extras);
    setSigns(combined);
    setFilteredSigns(combined);
  }, [apiSigns, v2Elements, v2FromUploads]);

  // Calculate map center. Prioritise the previous (DB/API) signals so the map
  // keeps focusing on the real data exactly like before; only fall back to the
  // combined set (v2 uploads / sample) when there are no API signals.
  useEffect(() => {
    const source = (apiSigns && apiSigns.length > 0) ? apiSigns : signs;
    if (source && source.length > 0) {
      // Filter out invalid coordinates
      const validSigns = source.filter(
        sign => sign.lat && sign.lng && !isNaN(sign.lat) && !isNaN(sign.lng)
      );

      if (validSigns.length > 0) {
        // Calculate average latitude and longitude
        const avgLat = validSigns.reduce((sum, sign) => sum + sign.lat, 0) / validSigns.length;
        const avgLng = validSigns.reduce((sum, sign) => sum + sign.lng, 0) / validSigns.length;

        const newCenter = [avgLat, avgLng];
        setMapCenter(newCenter);

        // Fly to the new center if map is already initialized
        if (mapRef.current) {
          mapRef.current.setView(newCenter, initialMapZoom);
        }
      }
    }
  }, [apiSigns, signs, initialMapZoom]);

  useEffect(() => {
    const fetchStreet = async () => {
      if (selectedSign) {
        setLoadingStreet(true);
        try {
          const data = await reverseGeocode(selectedSign.lat, selectedSign.lng);
          const roadName =
            data.road ||
            data.fullAddress ||
            "Unknown street";
          setStreetName(roadName);
        } catch (error) {
          console.error("Error during reverse geocoding:", error);
          setStreetName("Unknown street");
        } finally {
          setLoadingStreet(false);
        }
      }
    };

    fetchStreet();
  }, [selectedSign]);

  const focusedSignId = formOpen && !addNew ? (selectedSign?.id ?? null) : null;

  const handleMarkerClick = (sign) => {
    setFormOpen(false);
    setSelectedSign(sign);
    setAddNew(false);
    if (mapRef.current) {
      mapRef.current.flyTo([sign.lat, sign.lng], 18);
    }
    setFormOpen(true);
  };

  const handleDownload = () => {
    const jsonData = JSON.stringify(signs, null, 2);
    const blob = new Blob([jsonData], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "traffic_signs_data.json";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const searchLocation = async (query) => {
    if (!query || query.length < 3) {
      setSearchOptions([]);
      return;
    }

    setSearchLoading(true);
    try {
      const data = await searchGeocode(query, { limit: 5 });

      const options = data.map((item) => ({
        value: item.display_name,
        coordinates: [parseFloat(item.lat), parseFloat(item.lon)],
        data: item,
      }));

      setSearchOptions(options);
    } catch (error) {
      console.error("Error searching location:", error);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchValue(value);
    searchLocation(value);
  };

  const onSearchSelect = (value) => {
    const selectedOption = searchOptions.find((opt) => opt.value === value);
    if (selectedOption && mapRef.current) {
      mapRef.current.flyTo(selectedOption.coordinates, 18);
      L.marker(selectedOption.coordinates)
        .addTo(mapRef.current)
        .bindPopup(selectedOption.value)
        .openPopup();
    }
  };

  const handleAddSign = async (values, imagePreview, file) => {
    if (isCreatingSignal) return;
    setIsCreatingSignal(true);
    try {
      const formData = new FormData();
      formData.append("lat", values.lat);
      formData.append("lng", values.lng);
      formData.append("type", values.type);
      formData.append("description", signTypes[values.type]?.label || "");
      formData.append("street", values.indirizzo || "");

      if (values.id) formData.append("id", values.id);
      if (values.external_id)
        formData.append("external_id", values.external_id);
      if (values.lastInspection)
        formData.append("lastInspection", values.lastInspection);
      if (values.condition) formData.append("condition", values.condition);
      if (file) formData.append("image", file);

      let response;
      if (values.id) response = await updateSignal(formData, values.id);
      else response = await createSignal(formData);

      setFormOpen(false);
      setAddNew(false);
      setSelectedSign(null);
      fetchData();
      messageApi.success(t('trafficSign.signalCreatedSuccess'));
    } catch (error) {
      console.error("Error creating signal:", error);
      messageApi.error(t('trafficSign.signalCreatedError'));
    } finally {
      setIsCreatingSignal(false);
    }
  };

  const handleMapClick = (e) => {
    const { lat, lng } = e.latlng;

    // Handle measurement mode clicks
    if (mapMeasurementMode && mapView !== "pointcloud") {
      const mapCenter = mapRef.current.getCenter();

      // Convert lat/lng to approximate 3D coordinates
      const x = (lng - mapCenter.lng) * 111000 * Math.cos(mapCenter.lat * Math.PI / 180);
      const y = (lat - mapCenter.lat) * 111000;
      const z = 0; // Assume ground level for map measurements

      const clickedPoint = [x, y, z];

      if (mapMeasurementMode === 'distance') {
        if (mapMeasurementPoints.length < 2) {
          const newPoints = [...mapMeasurementPoints, clickedPoint];
          setMapMeasurementPoints(newPoints);

          if (newPoints.length === 2) {
            const distance = calculateDistance3D(newPoints[0], newPoints[1]);
            const newMeasurement = {
              type: 'distance',
              points: newPoints,
              value: distance,
              formattedValue: formatDistance(distance)
            };
            setMapMeasurements(prev => [...prev, newMeasurement]);
            setMapMeasurementPoints([]);
          }
        }
      } else if (mapMeasurementMode === 'area' || mapMeasurementMode === 'volume') {
        const newPoints = [...mapMeasurementPoints, clickedPoint];
        setMapMeasurementPoints(newPoints);
      }

      return; // Don't create a sign marker in measurement mode
    }

    // Original sign creation logic
    if (selectedSign != null) {
      setSelectedSign({ ...selectedSign, lat, lng });
      return;
    }
    setSelectedSign({
      external_id: ``,
      lat,
      lng,
      type: "",
      description: "",
      image_url: "",
      street: "",
      lastInspection: "",
      condition: "",
    });
  };

  const onImageClick = (imageUrl) => {
    if (!imageUrl) return;
    setPreviewImage(imageUrl);
    setPreviewVisible(true);
  };

  const handlePreviewCancel = () => {
    setPreviewVisible(false);
    setPreviewImage("");
  };

  const handleView3D = (sign) => {
    // For now, just show a message that sign 3D view is not available
    // since we removed signs from 3D view
    console.log("Sign 3D view requested but signs have been removed from 3D view");
    // You could show a message to the user here if needed
    // messageApi.info("3D view is only available for point cloud locations");
  };

  const handleDeleteClick = (sign) => {
    setSignToDelete(sign);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!signToDelete) return;

    setIsDeletingSignal(true);
    try {
      await deleteSignal(signToDelete.id);
      setDeleteModalVisible(false);
      setSignToDelete(null);
      setSelectedSign(null);
      setFormOpen(false);
      await fetchData(); // Refresh the signs list
      messageApi.success(t('trafficSign.signalDeletedSuccess') || 'Segnale eliminato con successo');
    } catch (error) {
      console.error("Error deleting signal:", error);
      messageApi.error(t('trafficSign.signalDeletedError') || 'Errore durante l\'eliminazione del segnale');
    } finally {
      setIsDeletingSignal(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setSignToDelete(null);
  };

  const handlePly3DMarkerClick = (plyLocation) => {
    // Set the selected PLY location
    setSelectedPlyLocation(plyLocation);
    // Switch to point cloud view
    setMapView("pointcloud");
    // Hide 3D markers when viewing point cloud
    setShow3DMarkers(false);

    console.log(`Loading point cloud for location: ${plyLocation.name}`);
  };

  // Add keyboard event listener for ESC key to exit 3D view
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && mapView === "pointcloud") {
        // Exit 3D view and reset to map view
        setMapView("map");
        setSelectedPlyLocation(null);
        setShow3DMarkers(true);

        // Reset map to initial center and zoom
        if (mapRef.current) {
          mapRef.current.setView(mapCenter, initialMapZoom);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [mapView, mapCenter, initialMapZoom]);

  // Handle traffic light animation state changes from SimulazioneModal
  const handleTlgwAnimationStateChange = (stateMap) => {
    setTlgwAnimationStates(stateMap);
  };


  // Logic to load traffic lights from current signs for Green Wave calculation
  const handleCaricaSignals = () => {
    if (!signs || signs.length === 0) {
      message.warning("Nessun segnale caricato");
      return;
    }
    // Filter only traffic signals if needed, or take all
    const trafficSemafori = signs.filter(s => 
      s.type?.toLowerCase().includes('semafor') || 
      s.type?.toLowerCase().includes('traffic-signal')
    ).map(s => ({
      id: s.id,
      lat: s.lat,
      lon: s.lng,
      nome: s.description || s.id,
      tags: { highway: 'traffic_signals' }
    }));

    if (trafficSemafori.length === 0) {
      // If no explicit traffic signals, allow using all signs as nodes if within polygon
      const allSemafori = signs.map(s => ({
        id: s.id,
        lat: s.lat,
        lon: s.lng,
        nome: s.description || s.id,
        tags: { highway: 'traffic_signals' }
      }));
      
      // Filter by polygon if active
      if (tlgwPolygonPoints.length >= 3) {
        const filtered = allSemafori.filter(s => {
          // point-in-polygon test
          const [y, x] = [s.lat, s.lon];
          let inside = false;
          for (let i = 0, j = tlgwPolygonPoints.length - 1; i < tlgwPolygonPoints.length; j = i++) {
            const [yi, xi] = tlgwPolygonPoints[i];
            const [yj, xj] = tlgwPolygonPoints[j];
            const intersect = ((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
            if (intersect) inside = !inside;
          }
          return inside;
        });
        setTlgwTrafficLights(filtered);
      } else {
        setTlgwTrafficLights(allSemafori);
      }
    } else {
      setTlgwTrafficLights(trafficSemafori);
    }
    
    // Switch to selection mode
    setTlgwIsSelecting(true);
  };

  const handleClearTlgw = () => {
    setTlgwPolygonPoints([]);
    setTlgwTrafficLights([]);
    setTlgwIsDrawing(false);
    setTlgwIsSelecting(false);
    setTlgwSelectedLights([]);
    setTlgwAnimationStates(null);
    if (tlgwFeatureGroupRef.current) {
      tlgwFeatureGroupRef.current.clearLayers();
    }
    tlgwDrawnLayerRef.current = null;
  };

  // Drawing Handlers for SignalFix (SyncFix)
  const handleStartPolygonDraw = useCallback(() => {
    if (!mapRef.current) return;

    if (tlgwDrawHandlerRef.current) {
      tlgwDrawHandlerRef.current.disable();
    }

    if (tlgwFeatureGroupRef.current && tlgwDrawnLayerRef.current) {
      tlgwFeatureGroupRef.current.removeLayer(tlgwDrawnLayerRef.current);
      tlgwDrawnLayerRef.current = null;
    }
    setTlgwPolygonPoints([]);

    const drawHandler = new L.Draw.Polygon(mapRef.current, {
      allowIntersection: false,
      showArea: true,
      shapeOptions: {
        color: '#8C74FF',
        weight: 2,
        fillColor: '#8C74FF',
        fillOpacity: 0.2
      }
    });

    tlgwDrawHandlerRef.current = drawHandler;
    drawHandler.enable();
    setTlgwIsDrawing(true);
  }, []);

  const handleStartPolygonEdit = useCallback(() => {
    if (!mapRef.current || !tlgwDrawnLayerRef.current) return;

    if (tlgwIsEditingRef.current) {
      if (tlgwDrawHandlerRef.current) {
        tlgwDrawHandlerRef.current.save();
        tlgwDrawHandlerRef.current.disable();
        tlgwDrawHandlerRef.current = null;
      }
      setTlgwIsEditing(false);
      tlgwIsEditingRef.current = false;
    } else {
      const editHandler = new L.EditToolbar.Edit(mapRef.current, {
        featureGroup: tlgwFeatureGroupRef.current,
        selectedPathOptions: {
          color: '#8C74FF',
          weight: 4,
          dashArray: '10, 10'
        }
      });

      tlgwDrawHandlerRef.current = editHandler;
      editHandler.enable();
      setTlgwIsEditing(true);
      tlgwIsEditingRef.current = true;
    }
  }, []);

  // Set up drawing event listeners
  useEffect(() => {
    if (mapRef.current) {
      const map = mapRef.current;

      const handleCreated = (e) => {
        const layer = e.layer;
        if (tlgwFeatureGroupRef.current) {
          if (tlgwDrawnLayerRef.current) {
            tlgwFeatureGroupRef.current.removeLayer(tlgwDrawnLayerRef.current);
          }
          tlgwFeatureGroupRef.current.addLayer(layer);
        }
        tlgwDrawnLayerRef.current = layer;
        tlgwIsEditingRef.current = false;

        const latlngs = layer.getLatLngs()[0];
        const points = latlngs.map(latlng => [latlng.lat, latlng.lng]);
        setTlgwPolygonPoints(points);
        setTlgwIsDrawing(false);
        tlgwDrawHandlerRef.current = null;
      };

      const handleEdited = (e) => {
        const layers = e.layers;
        layers.eachLayer((layer) => {
          if (layer === tlgwDrawnLayerRef.current) {
            const latlngs = layer.getLatLngs()[0];
            const points = latlngs.map(latlng => [latlng.lat, latlng.lng]);
            setTlgwPolygonPoints(points);
          }
        });
      };

      map.on(L.Draw.Event.CREATED, handleCreated);
      map.on(L.Draw.Event.EDITED, handleEdited);

      return () => {
        map.off(L.Draw.Event.CREATED, handleCreated);
        map.off(L.Draw.Event.EDITED, handleEdited);
      };
    }
  }, [mapRef.current, tlgwIsEditingRef]);

  // Category filter (from the legend) toggles whole element families. It is
  // applied on top of the per-type filter (filteredSigns) as a display filter.
  const handleToggleCategory = useCallback((key, enabled) => {
    setEnabledCategories((prev) => {
      if (key === "all") {
        return Object.fromEntries(CATEGORY_FILTERS.map((c) => [c.key, enabled]));
      }
      const next = { ...prev, [key]: enabled };
      next.all = CATEGORY_FILTERS.filter((c) => !c.master).every((c) => next[c.key]);
      return next;
    });
  }, []);

  const enabledCategorySet = useMemo(
    () => new Set(Object.keys(enabledCategories).filter((k) => enabledCategories[k])),
    [enabledCategories]
  );

  const displaySigns = useMemo(
    () => (filteredSigns || []).filter((sign) => isSignInEnabledCategories(sign, enabledCategorySet)),
    [filteredSigns, enabledCategorySet]
  );

  // Elements with a polyline geometry are drawn by the geometry layer; the
  // rest are point markers that participate in clustering.
  const pointSigns = useMemo(
    () => displaySigns.filter((sign) => !hasRenderableGeometry(sign)),
    [displaySigns]
  );
  const geometrySigns = useMemo(
    () => displaySigns.filter((sign) => hasRenderableGeometry(sign)),
    [displaySigns]
  );

  return (
    <div className="signal-fix-fullscreen-container">
      {contextHolder}

      {/* Original SignalFix toolbar - search, filter, car path, add, download */}
      <SignalFixExtras
        signs={signs}
        setAddNew={setAddNew}
        setFilteredSigns={setFilteredSigns}
        setFilterActivated={setFilterActivated}
        onLocationSearch={(val) => setSearchValue(val)}
        onLocationSelect={(location) => {
          if (location?.coordinates && mapRef.current) {
            mapRef.current.flyTo(location.coordinates, 17);
          }
        }}
        show3DMarkers={show3DMarkers}
        onToggle3DMarkers={setShow3DMarkers}
        showSegmentiRms={showSegmentiRms}
        onToggleSegmentiRms={setShowSegmentiRms}
      />

      <div className="signal-fix-fullscreen-map">
        <MapContainer
          center={mapCenter}
          zoom={initialMapZoom}
          className="map-container-fullscreen"
          ref={mapRef}
          id={filterActivated ? "map-filter-activated" : "map-no-filter"}
          whenCreated={(map) => {
            mapRef.current = map;
          }}
          zoomControl={false}
        >
          {mapView !== "pointcloud" && (
            <TraffixTileLayer theme={mapTheme} />
          )}
          <MapClickDetector
            onMapClick={handleMapClick}
            measurementMode={mapMeasurementMode}
            measurementPoints={mapMeasurementPoints}
            onCompleteMeasurement={(mode, points) => {
              if (mode === 'area') {
                const area = calculatePolygonArea(points);
                const newMeasurement = {
                  type: 'area',
                  points: points,
                  value: area,
                  formattedValue: formatArea(area)
                };
                setMapMeasurements(prev => [...prev, newMeasurement]);
                setMapMeasurementPoints([]);
              } else if (mode === 'volume') {
                // For volume, we'd need to show a height input dialog
                const height = 5; // Default height
                const volume = calculateVolume(points, height);
                const newMeasurement = {
                  type: 'volume',
                  points: points,
                  height: height,
                  value: volume,
                  formattedValue: formatVolume(volume)
                };
                setMapMeasurements(prev => [...prev, newMeasurement]);
                setMapMeasurementPoints([]);
              }
            }}
          />
          <ZoomLimitsController />
          <FeatureGroup ref={tlgwFeatureGroupRef} />

          {/* Map Measurement Indicators */}
          {mapView !== "pointcloud" && (
            <MapMeasurementIndicators
              measurementMode={mapMeasurementMode}
              measurementPoints={mapMeasurementPoints}
              measurements={mapMeasurements}
              showPointCloud={mapView === "pointcloud"}
            />
          )}

          {/* Segmenti RMS GeoJSON - only show in 2D maps */}
          {(() => {
            const shouldShow = mapView !== "pointcloud" && showSegmentiRms && segmentiRmsGeoJson;

            if (shouldShow) {
              return (
                <GeoJSON
                  key="segmenti-rms"
                  data={segmentiRmsGeoJson}
                  style={(feature) => {
                    // Use the color from the properties or default
                    const color = feature?.properties?.color || '#f0c20c99';
                    return {
                      color: color,
                      weight: 6,
                      opacity: 0.9,
                      lineJoin: 'round',
                      lineCap: 'round'
                    };
                  }}
                  onEachFeature={(feature, layer) => {
                    // Add popup with RMS data using the custom tooltip component
                    if (feature.properties) {
                      const popupContent = RmsSegmentTooltip({ feature });
                      layer.bindPopup(popupContent, {
                        maxWidth: 280,
                        minWidth: 250,
                        className: 'rms-segment-popup',
                        closeButton: true,
                        autoPan: true,
                        offset: L.point(15, 0) // Offset 15px to the right
                      });
                    }
                  }}
                />
              );
            }
            return null;
          })()}

          <DeckGLPointCloudOverlay
            showPointCloud={mapView === "pointcloud"}
            pointSize={pointCloudSize}
            onPointSizeChange={setPointCloudSize}
            selectedPlyLocation={selectedPlyLocation}
          />
          {/* Segmented elements + on-road horizontal signage (drawn as geometry) */}
          {mapView !== "pointcloud" && (
            <SignalFixGeometryLayer
              signs={geometrySigns}
              onMarkerClick={handleMarkerClick}
              onDelete={handleDeleteClick}
              onView3D={handleView3D}
            />
          )}

          {mapView !== "pointcloud" && (filterActivated ? (
            pointSigns.map((sign) => {
              if (
                !sign?.lat ||
                !sign?.lng ||
                isNaN(sign.lat) ||
                isNaN(sign.lng)
              ) {
                console.warn("Invalid coordinates for sign:", sign);
                return null;
              }

              return (
                <TrafficSignMarker
                  key={sign.id}
                  sign={sign}
                  onMarkerClick={handleMarkerClick}
                  isSelected={selectedSign?.id === sign.id}
                  isMuted={Boolean(focusedSignId) && focusedSignId !== sign.id}
                  onView3D={handleView3D}
                  onDelete={handleDeleteClick}
                  animationState={tlgwAnimationStates?.[sign.id]}
                />
              );
            })
          ) : (
            <MarkerClusterGroup
              chunkedLoading
              disableClusteringAtZoom={17}
              maxClusterRadius={40}
              iconCreateFunction={createClusterIcon}
            >
              {pointSigns.map((sign) => {
                if (
                  !sign?.lat ||
                  !sign?.lng ||
                  isNaN(sign.lat) ||
                  isNaN(sign.lng)
                ) {
                  console.warn("Invalid coordinates for sign:", sign);
                  return null;
                }

                return (
                  <TrafficSignMarker
                    key={sign.id}
                    sign={sign}
                    onMarkerClick={handleMarkerClick}
                    isSelected={selectedSign?.id === sign.id}
                    isMuted={Boolean(focusedSignId) && focusedSignId !== sign.id}
                    onView3D={handleView3D}
                    onDelete={handleDeleteClick}
                    animationState={tlgwAnimationStates?.[sign.id]}
                />
                );
              })}
            </MarkerClusterGroup>
          ))}

          {/* Add 3D PLY Markers */}
          {mapView !== "pointcloud" && (
            <Ply3DMarkers
              showMarkers={show3DMarkers}
              onMarkerClick={handlePly3DMarkerClick}
            />
          )}

          <div className="signal-fix-map-controls">
            <CombinedViewToggleButton
              mode={mapView}
              onCycle={cycleViewMode}
            />
            <CurrentLocationButton />
            <ZoomButtons />
          </div>

          {/* Theme toggle and Satellite view toggle */}
          {mapView !== "pointcloud" && (
            <div className="theme-toggle-container" style={{ bottom: "200px", right: "18px", display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <ThemeToggleButton theme={mapTheme} onChange={setMapTheme} />
            </div>
          )}

        </MapContainer>

        {/* Campaign summary (top-left, below the search) and legend
            (bottom-left, expands upward). Both outside MapContainer so clicks
            don't bubble to the map's sign-creation handler. */}
        {mapView !== "pointcloud" && (
          <>
            <div className="signalfix-campaign-wrap">
              <CampaignSummaryPanel
                distance={campaignInfo.distance}
                location={campaignInfo.location}
                date={campaignInfo.date}
                loading={campaignInfo.loading}
              />
            </div>
            <SignalFixLegend
              categories={enabledCategories}
              onToggleCategory={handleToggleCategory}
            />
          </>
        )}

        {/* Map Measurement Tools (only in 2D views) - positioned outside MapContainer */}
        {mapView !== "pointcloud" && (
          <>
            <MeasurementTools
              measurementMode={mapMeasurementMode}
              onModeChange={(mode) => {
                setMapMeasurementMode(mode);
                if (mode === null) {
                  setMapMeasurementPoints([]);
                }
              }}
              measurements={mapMeasurements}
              onClearMeasurements={() => {
                setMapMeasurements([]);
                setMapMeasurementPoints([]);
              }}
              onUndo={() => {
                if (mapMeasurements.length > 0) {
                  setMapMeasurements(mapMeasurements.slice(0, -1));
                }
              }}
              canUndo={mapMeasurements.length > 0}
            />

            {/* Map Selected Points Display */}
            {mapMeasurementPoints.length > 0 && (
              <SelectedPointsDisplay
                measurementPoints={mapMeasurementPoints}
                onRemovePoint={(index) => {
                  const newPoints = [...mapMeasurementPoints];
                  newPoints.splice(index, 1);
                  setMapMeasurementPoints(newPoints);
                }}
                measurementMode={mapMeasurementMode}
                onClearAllPoints={() => setMapMeasurementPoints([])}
              />
            )}
          </>
        )}
      </div>
      <div className="overlay-elements">
        {(formOpen || addNew) && (
          <div className="form-overlay">
            <CreateSignal
              selectedValues={selectedSign}
              handleAddSign={handleAddSign}
              isCreating={isCreatingSignal}
              setSelectedSign={setSelectedSign}
              onClose={() => {
                setFormOpen(false);
                setAddNew(false);
                setSelectedSign(null);
              }}
            />
          </div>
        )}
      </div>

      {/* Module Slider - Navigation between modules */}
      <ModuleSlider
        showNavbarControls={false}
        setShowNavbarControls={() => {}}
        showSimfixAreas={false}
        setShowSimfixAreas={() => {}}
        showPredictMode={false}
        setShowPredictMode={() => {}}
        showTlGreenWave={false}
        setShowTlGreenWave={() => {}}
        showAssignx={false}
        setShowAssignx={() => {}}
      />


      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        visible={deleteModalVisible}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        signData={signToDelete}
        loading={isDeletingSignal}
      />

      {/* TL Green Wave Controls - disabled on SignalFix page (uses its own toolbar) */}
      <TlGreenWaveControls
        isActive={false}
        isDrawing={tlgwIsDrawing}
        setIsDrawing={setTlgwIsDrawing}
        polygonPoints={tlgwPolygonPoints}
        trafficLights={tlgwTrafficLights}
        isAddingManualPoint={false}
        setIsAddingManualPoint={() => {}}
        onLoadTrafficLights={handleCaricaSignals}
        onClearAll={handleClearTlgw}
        isLoadingCarica={false}
        isSelecting={tlgwIsSelecting}
        setIsSelecting={setTlgwIsSelecting}
        selectedLights={tlgwSelectedLights}
        onRemoveLight={(id) => setTlgwSelectedLights(prev => prev.filter(lid => lid !== id))}
        onClearSelection={() => setTlgwSelectedLights([])}
        onStartDraw={handleStartPolygonDraw}
        onTrafficLightStateChange={handleTlgwAnimationStateChange}
        persistedSemaforiConfig={null}
        setPersistedSemaforiConfig={() => {}}
        persistedLinksConfig={null}
        setPersistedLinksConfig={() => {}}
        persistedVelocity={50}
        setPersistedVelocity={() => {}}
        isEditing={tlgwIsEditing}
        onStartEdit={handleStartPolygonEdit}
      />
    </div>
  );
}

export default SignalFix;
